/**
 * Build-time feature flags for the GeoViewer frontend.
 *
 * Flags are read once at module load from `import.meta.env`. Defaults are
 * conservative: every flag added here defaults OFF unless production rollout
 * is explicitly approved by the release closure decision log.
 *
 * Per WP-B3 / DEC-7 the `INTAKE_HANDOFF_REAL` flag governs whether the new
 * `/intake/handoff` route ingests handoffs from GeoConverter for real or
 * renders the safe-fallback "Intake disabled" notice (the rollback path).
 * Flag default OFF in 2026-Q2; flip to `1` only after WP-B4 end-to-end
 * verification.
 *
 * This file is the single ingress for build-time flag values; consumers
 * (route components, hooks) MUST import from here so a future runtime
 * entitlement layer can be added without touching the call sites.
 */

/**
 * Real handoff intake (WP-B3 / DEC-7).
 *
 * - `0` / unset → route renders the rollback "disabled" banner (no fetch,
 *   no ack, no layer dispatch). The route still resolves so deep links
 *   work, but the body explains that intake is disabled in this build.
 * - `1`         → real intake flow runs: fetch handoff, validate against
 *   `@geosoft/converter-viewer-handoff`, map to viewer Layers, accept/reject.
 */
export const INTAKE_HANDOFF_REAL: boolean =
  import.meta.env.VITE_INTAKE_HANDOFF_REAL === '1';

/**
 * True 3D relief terrain via Giro3D `ElevationLayer` (spike for gap G20 —
 * open half of "true 3D relief + vertical exaggeration").
 *
 * - `0` / unset → `kind: 'terrain'` routes to the stable flat
 *   `terrain-color-source` ColorLayer (TiTiler `colormap_name=terrain`).
 *   Vertical exaggeration stays disabled in `TerrainToolCard` because scaling
 *   a flat (zero-height) mesh is a no-op.
 * - `1`         → `kind: 'terrain'` routes to the `terrain-source`
 *   `ElevationLayer` path (real DEM heights → 3D mesh) and the vertical-
 *   exaggeration control is enabled.
 *
 * DEM-like *rasters* (DTM/DEM COGs detected by `isDemLikeLayer`) always use the
 * ElevationLayer mesh in Giro3D regardless of this flag — that is the ArcGIS
 * Pro–style 3D view path when the operator switches MapLibre → Giro3D.
 */
export const TERRAIN_ELEVATION_MESH: boolean =
  import.meta.env.VITE_FEATURE_TERRAIN_ELEVATION_MESH === '1';
